import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { MovableDocumentCategory } from '@hector/shared-types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConnectedDestinationPicker } from './ConnectedDestinationPicker';
import { useDestinationSelection } from './useDestinationSelection';
import { useDestinationSubcategory } from './useDestinationSubcategory';
import type { DestinationValue } from './destinationModel';

/**
 * LE dialogue « où ranger cette pièce ? », partagé par tous les écrans.
 *
 * ─── Pourquoi un seul dialogue et pas seulement un sélecteur partagé ──────
 * Quatre écrans posaient la même question de quatre façons, et il n'existe que
 * DEUX variantes réelles : avec ou sans nom de pièce. Partager le seul sélecteur
 * aurait laissé chaque écran réinventer le reste — l'ordre des champs, le libellé
 * du bouton, la remise à zéro à l'ouverture, la validation, l'état d'attente. Ce
 * sont précisément les endroits où ils divergeaient.
 *
 * Un écran ne fournit donc plus que trois choses : son titre, ses valeurs
 * présélectionnées, et ce qu'il fait au moment d'enregistrer.
 *
 * ⚠️ La remise à zéro à l'ouverture est portée ICI, pas par les appelants : ces
 * fenêtres restent MONTÉES en permanence (le panneau de décision vit dans la coque
 * globale). Sans elle, un second enregistrement rouvrirait sur les choix du
 * précédent — et rangerait la pièce dans le dossier d'un autre client sans que
 * rien ne l'annonce.
 */
export interface SaveToDestinationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  /** Libellé du bouton de validation (« Enregistrer », « Déplacer »…). */
  confirmLabel: string;
  /**
   * Demande un nom de pièce. Absent = le nom est calculé par l'appelant (une
   * décision de jurisprudence porte déjà son intitulé canonique).
   */
  nameField?: {
    label: string;
    placeholder?: string;
    /** Valeur proposée à l'ouverture. */
    initialValue?: string;
  };
  /** Dossier présélectionné — dossier courant, ou dossier de la conversation. */
  initialCaseId?: string;
  /** Onglet présélectionné, posé d'emblée même avant qu'un dossier soit choisi. */
  defaultCategory?: MovableDocumentCategory;
  categories?: readonly MovableDocumentCategory[];
  /** Destination interdite, et pourquoi (cf. `DestinationPicker`). */
  isPathDisabled?: (path: string[], value: DestinationValue) => string | null;
  notice?: React.ReactNode | ((value: DestinationValue) => React.ReactNode);
  /** Voir `DestinationPicker.caseLocked` — « Déplacer vers » ne change pas de dossier. */
  caseLocked?: boolean;
  /** Voir `DestinationPicker.expandPath` — la branche d'origine, dépliée d'emblée. */
  expandPath?: readonly string[];
  /**
   * Suit la sélection en cours.
   *
   * WHY cette fuite d'état, alors que `onConfirm` porte déjà la destination : des
   * appelants ont besoin du dossier choisi AU RENDU, pas seulement à la validation
   * — `useBatchSynthesis(caseId)` scope ses invalidations et les métadonnées du job
   * sur lui. Sans ça, enregistrer dans un autre dossier que le présélectionné
   * rafraîchirait le mauvais écran et étiquetterait le job du mauvais nom.
   */
  onSelectionChange?: (destination: DestinationValue) => void;
  /**
   * Écrit réellement. Une erreur laisse le dialogue OUVERT, choix intacts.
   *
   * `subcategory` est le tag « Partie adverse » dérivé du dossier choisi
   * (`actes_communiques` / `pieces_communiquees`), `null` partout ailleurs —
   * cf. `useDestinationSubcategory`.
   */
  onConfirm: (result: {
    destination: DestinationValue;
    name: string;
    subcategory: string | null;
  }) => Promise<void>;
}

export function SaveToDestinationDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  nameField,
  initialCaseId,
  defaultCategory,
  categories,
  isPathDisabled,
  notice,
  caseLocked,
  expandPath,
  onSelectionChange,
  onConfirm,
}: SaveToDestinationDialogProps) {
  const selection = useDestinationSelection({ initialCaseId, defaultCategory, resetToken: open });
  const [name, setName] = useState(nameField?.initialValue ?? '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) setName(nameField?.initialValue ?? '');
  }, [open, nameField?.initialValue]);

  const { value: selectionValue } = selection;
  useEffect(() => {
    onSelectionChange?.(selectionValue);
  }, [selectionValue, onSelectionChange]);

  const subcategory = useDestinationSubcategory(selectionValue);
  const nameMissing = Boolean(nameField) && name.trim().length === 0;
  // Un dossier « Partie adverse » dont le type est indéterminable ne peut pas
  // recevoir de pièce : le tag qu'elle porterait est précisément ce qui la rend
  // visible dans l'onglet. Cas réel — une paire legacy jamais taguée, renommée
  // depuis. Refuser ici vaut mieux qu'écrire une pièce introuvable.
  const untypedPartieAdverse =
    selectionValue.category === 'partie_adverse'
    && selectionValue.folderPath.length > 0
    && subcategory === null;
  // Une destination interdite peut aussi être celle de DÉPART : « Déplacer vers »
  // grise l'emplacement actuel dans l'arbre, mais c'est lui qui est sélectionné à
  // l'ouverture. Sans cette relecture, le bouton proposerait un déplacement qui
  // ne déplace rien.
  //
  // Conséquence à assumer : la fenêtre s'OUVRE dans cet état, avant tout geste.
  // La phrase doit donc décrire une situation et dire quoi faire, jamais
  // constater un échec — « Destination impossible », lu à l'ouverture, donnait
  // le sentiment que la section entière était interdite (retour d'usage
  // 2026-09-15 : « je ne peux pas enregistrer dans Mes pièces »), alors que
  // seule la ligne d'où part la pièce l'est.
  const selectedPathRefusal = isPathDisabled?.(selectionValue.folderPath, selectionValue) ?? null;
  // Le refus porte sa RAISON : un bouton grisé muet force l'avocat à deviner, et
  // une infobulle ne s'ouvre pas au clavier sur un bouton désactivé.
  const issue =
    (nameMissing ? `${nameField?.label} est requis.` : null)
    ?? selection.issue
    ?? (untypedPartieAdverse
      ? 'Ce dossier n\'est ni « Écritures » ni « Pièces adverses » : choisissez-en un qui porte l\'un des deux badges.'
      : null)
    ?? (selectedPathRefusal ? `Choisissez une autre destination (${selectedPathRefusal}).` : null);

  const handleConfirm = async () => {
    if (issue || isSaving) return;
    setIsSaving(true);
    try {
      await onConfirm({ destination: selection.value, name: name.trim(), subcategory });
      onOpenChange(false);
    } catch {
      // L'appelant a déjà signalé l'erreur (toast). On garde la fenêtre ouverte
      // avec ses choix : refaire trois sélections après un échec réseau serait
      // une punition pour une panne qui ne vient pas de l'avocat.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* z-panelDialog : ces dialogues s'ouvrent depuis des panneaux latéraux
          (décision, canvas) qui vivent au-dessus du z-index par défaut.

          `flex flex-col` + `overflow-y-hidden` : la carte par défaut est une
          GRILLE qui défile en entier (`overflow-y-auto` de la primitive). Avec un
          champ de nom en plus — « Sauvegarder dans le dossier », « Ajouter comme
          pièce au dossier » — le pied passait sous le pli : il fallait faire
          défiler la fenêtre pour trouver Annuler et Enregistrer. Ici le titre et
          le pied ne défilent JAMAIS, seul le milieu le fait. (`overflow-y-hidden`
          et non `overflow-hidden` : seul le même axe remplace celui de la
          primitive dans la fusion Tailwind.) */}
      <DialogContent
        className="max-w-lg flex flex-col overflow-y-hidden z-panelDialog"
        overlayClassName="z-panelDialog"
      >
        <DialogHeader className="min-w-0 flex-none">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription className="break-words">{description}</DialogDescription>}
        </DialogHeader>

        {/* `min-h-0` : sans lui, un élément flex ne descend pas sous la hauteur
            de son contenu et c'est la carte entière qui déborderait à nouveau. */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-2">
          {nameField && (
            <div className="space-y-2">
              <Label htmlFor="destination-name">{nameField.label}</Label>
              <Input
                id="destination-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={nameField.placeholder}
                autoFocus
              />
            </div>
          )}

          <ConnectedDestinationPicker
            value={selection.value}
            onCaseChange={selection.setCaseId}
            onCategoryChange={selection.setCategory}
            onFolderPathChange={selection.setFolderPath}
            categories={categories}
            isPathDisabled={isPathDisabled}
            notice={notice}
            caseLocked={caseLocked}
            expandPath={expandPath}
          />
        </div>

        {/* Le pied en DEUX blocs, et non en trois éléments frères (2026-09-15) :
            la raison du refus à gauche, les deux boutons ensemble à droite.
            Frères dans un conteneur `flex-wrap`, la phrase poussait les boutons
            au-delà de la largeur de la carte et « Déplacer » passait SOUS
            « Annuler » — vu sur « Choisissez une autre destination (emplacement
            actuel). », qui est précisément la phrase affichée à l'ouverture.
            La phrase prend donc `min-w-0 flex-1` : c'est ELLE qui se replie sur
            deux lignes, jamais le groupe de boutons, marqué `flex-none`. */}
        <DialogFooter className="min-w-0 flex-none sm:flex-wrap sm:space-x-0 sm:gap-3">
          {issue && (
            <p className="min-w-0 flex-1 text-xs text-grey-600 sm:self-center" role="status">
              {issue}
            </p>
          )}
          <div className="flex flex-none items-center justify-end gap-2 sm:ml-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Annuler
            </Button>
            <Button onClick={handleConfirm} disabled={Boolean(issue) || isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {confirmLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
